import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { useNotification } from '../contexts/NotificationContext';
import { useTranslation } from '../i18n';
import { PageHeader } from './PageHeader';
import type { JournalEntry } from '../data/mockData';
import './AddJournalEntryPage.css';

export const AddJournalEntryPage: React.FC = () => {
  const navigate = useNavigate();
  const { updateBalance } = useUser();
  const { showSuccess } = useNotification();
  const t = useTranslation();
  const [selectedMood, setSelectedMood] = useState<JournalEntry['mood'] | ''>('');
  const [note, setNote] = useState('');
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const moods: Array<{ id: JournalEntry['mood']; label: string; emoji: string }> = [
    { id: 'great', label: t.journal.great, emoji: '😊' },
    { id: 'good', label: t.journal.good, emoji: '🙂' },
    { id: 'okay', label: t.journal.okay, emoji: '😐' },
    { id: 'bad', label: t.journal.bad, emoji: '😟' },
    { id: 'terrible', label: t.journal.terrible, emoji: '😢' }
  ];

  const activities = [
    { id: 'meditation', label: t.journal.meditationActivity },
    { id: 'work', label: t.journal.work },
    { id: 'reading', label: t.journal.reading },
    { id: 'yoga', label: t.journal.yoga },
    { id: 'social', label: t.journal.social },
    { id: 'walk', label: t.journal.walk }
  ];

  const handleActivityToggle = (activityId: string) => {
    setSelectedActivities(prev => 
      prev.includes(activityId)
        ? prev.filter(id => id !== activityId)
        : [...prev, activityId]
    );
  };

  const handleSave = async () => {
    if (!selectedMood) {
      alert(t.journal.selectMood);
      return;
    }

    if (!note.trim()) {
      alert(t.journal.addNote);
      return;
    }

    try {
      setSaving(true);
      const response = await apiClient.createJournalEntry({
        date: new Date().toISOString().split('T')[0],
        mood: selectedMood,
        note: note.trim(),
        activities: selectedActivities
      });

      showSuccess(t.journal.savedSuccessfully);
      
      // Check for completed tasks and show notifications
      if (response.data?.completed_tasks && response.data.completed_tasks.length > 0) {
        response.data.completed_tasks.forEach((task: any) => {
          showSuccess(
            `✨ "${task.title}" +${task.reward} 💎`,
            5000
          );
        });
        
        // Update balance if returned
        if (response.data?.balance !== undefined) {
          updateBalance(response.data.balance);
        }
      }
      
      // Small delay to show notifications before navigating
      setTimeout(() => {
        navigate('/journal');
      }, 500);
    } catch (error: any) {
      console.error('Failed to save journal entry:', error);
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert(t.journal.saveFailed);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (selectedMood || note || selectedActivities.length > 0) {
      if (window.confirm(t.journal.unsavedWarning)) {
        navigate('/journal');
      }
    } else {
      navigate('/journal');
    }
  };

  return (
    <div className="page-container add-journal-page">
      <PageHeader title={t.journal.newEntry} onBack={handleBack} />

      <div className="add-journal-content">
        <div className="form-section">
          <h2 className="section-title">{t.journal.howDoYouFeel}</h2>
          <div className="mood-selector">
            {moods.map(mood => (
              <button
                key={mood.id}
                className={`mood-button ${selectedMood === mood.id ? 'selected' : ''}`}
                onClick={() => setSelectedMood(mood.id)}
              >
                <span className="mood-emoji-large">{mood.emoji}</span>
                <span className="mood-label">{mood.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="form-section">
          <h2 className="section-title">{t.journal.note}</h2>
          <textarea
            className="note-input"
            placeholder={t.journal.notePlaceholder}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={6}
          />
        </div>

        <div className="form-section">
          <h2 className="section-title">{t.journal.activitiesOptional}</h2>
          <div className="activities-selector">
            {activities.map(activity => (
              <button
                key={activity.id}
                className={`activity-chip ${selectedActivities.includes(activity.id) ? 'selected' : ''}`}
                onClick={() => handleActivityToggle(activity.id)}
              >
                {activity.label}
              </button>
            ))}
          </div>
        </div>

        <button 
          className="save-button-bottom" 
          onClick={handleSave}
          disabled={!selectedMood || !note.trim() || saving}
        >
          {saving ? t.common.saving : t.common.save}
        </button>
      </div>
    </div>
  );
};
